import { Button } from '@/components/ui/button';
import { Download, FileText, File as FileIcon } from 'lucide-react';
import { Loader2 } from 'lucide-react';

export type FileCategory = 'image' | 'video' | 'pdf' | 'other';

export function getFileCategory(contentType: string): FileCategory {
  if (contentType.startsWith('image/')) return 'image';
  if (contentType.startsWith('video/')) return 'video';
  if (contentType === 'application/pdf') return 'pdf';
  return 'other';
}

export function isPreviewable(contentType: string): boolean {
  const category = getFileCategory(contentType);
  return category !== 'other';
}

interface FileViewerProps {
  blobUrl: string | null;
  isLoading: boolean;
  error: string | null;
  filename: string;
  contentType: string;
  onDownload: () => void;
  onImageLoad?: (naturalWidth: number, naturalHeight: number) => void;
  imageStyle?: React.CSSProperties;
}

export function FileViewer({ blobUrl, isLoading, error, filename, contentType, onDownload, onImageLoad, imageStyle }: FileViewerProps) {
  const fileCategory = getFileCategory(contentType);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !blobUrl) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
        <FileIcon className="h-12 w-12 mb-2" />
        <p className="text-sm">{error || 'Unable to preview'}</p>
        <Button variant="outline" size="sm" className="mt-4" onClick={onDownload}>
          <Download className="h-4 w-4 mr-2" />
          Download Instead
        </Button>
      </div>
    );
  }

  switch (fileCategory) {
    case 'image':
      return (
        <img
          src={blobUrl}
          alt={filename}
          style={imageStyle}
          className="object-contain mx-auto"
          onLoad={(e) => {
            const img = e.currentTarget;
            onImageLoad?.(img.naturalWidth, img.naturalHeight);
          }}
        />
      );

    case 'video':
      return (
        <video
          src={blobUrl}
          controls
          autoPlay
          className="max-w-full max-h-[80vh] mx-auto"
        >
          Your browser does not support video playback.
        </video>
      );

    case 'pdf':
      return (
        <object
          data={blobUrl}
          type="application/pdf"
          className="w-full h-[80vh] border-0"
        >
          <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
            <FileText className="h-12 w-12 mb-2" />
            <p className="text-sm mb-1">PDF preview is not available in this browser.</p>
            <Button variant="outline" size="sm" className="mt-4" onClick={onDownload}>
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
          </div>
        </object>
      );

    default:
      return (
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
          <FileText className="h-12 w-12 mb-2" />
          <p className="text-sm mb-1">{filename}</p>
          <p className="text-xs text-gray-500 mb-4">Preview not available for this file type</p>
          <Button variant="outline" size="sm" onClick={onDownload}>
            <Download className="h-4 w-4 mr-2" />
            Download File
          </Button>
        </div>
      );
  }
}
